import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, Plus, Trash2, ArrowUp, ArrowDown, Save, Wand2, Heading, ListChecks } from "lucide-react";

// Tabella confronto scritta a mano: righe e caselle libere, prezzi presi live dai pacchetti.
const fmt = (v) => `${new Intl.NumberFormat("it-IT").format(Math.round(Number(v) || 0))} €`;
const inp = "w-full bg-transparent outline-none rounded px-1.5 py-1 border border-transparent hover:border-zinc-300 focus:border-emerald-500 focus:bg-white placeholder:text-zinc-400";

function RowTools({ i, n, move, del }) {
  return (
    <div className="flex opacity-30 group-hover:opacity-100 transition-opacity">
      <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 hover:bg-zinc-100 rounded disabled:opacity-20" title="Sposta su" data-testid={`tab-su-${i}`}><ArrowUp className="h-3.5 w-3.5" /></button>
      <button onClick={() => move(i, 1)} disabled={i === n - 1} className="p-1 hover:bg-zinc-100 rounded disabled:opacity-20" title="Sposta giù" data-testid={`tab-giu-${i}`}><ArrowDown className="h-3.5 w-3.5" /></button>
      <button onClick={() => del(i)} className="p-1 hover:bg-rose-50 rounded text-rose-600" title="Elimina riga" data-testid={`tab-elimina-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
    </div>
  );
}

function Riga({ r, i, n, pkgs, set, move, del }) {
  const tools = <RowTools i={i} n={n} move={move} del={del} />;
  if (r.tipo === "sezione") return (
    <tr className="group" data-testid={`tab-riga-${i}`}>
      <td colSpan={pkgs.length + 1} className="pt-4 pb-1 px-1">
        <div className="flex items-center gap-1">
          <input value={r.nome} onChange={(e) => set(i, { nome: e.target.value })} placeholder="Titolo sezione (es. Pavimenti)" className={`${inp} text-[11px] font-bold uppercase tracking-wider`} data-testid={`tab-nome-${i}`} />{tools}
        </div>
      </td>
    </tr>
  );
  return (
    <tr className="group border-b border-zinc-100" data-testid={`tab-riga-${i}`}>
      <td className="px-1 py-1"><div className="flex items-center gap-1">
        <input value={r.nome} onChange={(e) => set(i, { nome: e.target.value })} placeholder="Voce (es. Pavimento)" className={`${inp} text-sm font-medium`} data-testid={`tab-nome-${i}`} />{tools}
      </div></td>
      {pkgs.map((p) => (
        <td key={p.id} className="px-1 py-1">
          <input value={r.celle?.[p.id] || ""} onChange={(e) => set(i, { celle: { ...r.celle, [p.id]: e.target.value } })}
            placeholder="—" className={`${inp} text-xs text-center`} data-testid={`tab-cella-${i}-${p.id}`} />
        </td>
      ))}
    </tr>
  );
}

function Promemoria({ pkgs, sugg }) {
  return (
    <details className="border-t px-5 py-3" data-testid="tab-promemoria">
      <summary className="cursor-pointer text-sm font-semibold flex items-center gap-2"><ListChecks className="h-4 w-4" />Promemoria: voci incluse oggi nei pacchetti</summary>
      <div className="grid gap-4 mt-3" style={{ gridTemplateColumns: `repeat(${pkgs.length}, minmax(0,1fr))` }}>
        {pkgs.map((p) => (
          <div key={p.id} className="text-xs">
            <div className="font-bold mb-1" style={{ color: p.color }}>{p.name}</div>
            <ul className="space-y-0.5 text-zinc-600">{(sugg[p.id] || []).map((s, k) => <li key={k}>• {s}</li>)}</ul>
          </div>
        ))}
      </div>
    </details>
  );
}

export default function BrochureRigheEditor() {
  const [data, setData] = useState(null);
  const [righe, setRighe] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/brochure/tabella").then((r) => { setData(r.data); setRighe(r.data.salvata ? r.data.righe : r.data.bozza); setDirty(false); })
    .catch(() => toast.error("Errore caricamento"));
  useEffect(() => { load(); }, []);
  const upd = (fn) => { setRighe(fn); setDirty(true); };
  const set = (i, patch) => upd((rs) => rs.map((r, k) => (k === i ? { ...r, ...patch } : r)));
  const move = (i, d) => upd((rs) => { const a = [...rs]; [a[i], a[i + d]] = [a[i + d], a[i]]; return a; });
  const del = (i) => upd((rs) => rs.filter((_, k) => k !== i));
  const add = (tipo) => upd((rs) => [...rs, { tipo, nome: "", celle: {} }]);
  const save = async () => {
    setSaving(true);
    try { await api.put("/brochure/tabella", { righe }); toast.success("Tabella salvata: la brochure ora usa questa"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Salvataggio non riuscito"); }
    setSaving(false);
  };
  const bozza = () => { if (window.confirm("Sostituire la tabella con la bozza ricavata dalle voci attuali dei pacchetti?")) upd(() => data.bozza); };
  if (!data) return <div className="flex items-center gap-2 text-sm text-zinc-500 py-6"><Loader2 className="h-4 w-4 animate-spin" />Caricamento…</div>;
  const pkgs = data.packages;
  return (
    <div className="bg-white border border-zinc-200 rounded-xl overflow-x-auto shadow-sm" data-testid="brochure-righe-editor">
      <div className="bg-zinc-900 text-white px-5 py-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xl font-extrabold">Scegli la tua ristrutturazione</div>
          <div className="text-xs text-zinc-300">{data.salvata ? "Tabella scritta a mano (usata nella brochure)" : "Bozza dalle voci dei pacchetti: modificala e salva"}</div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" onClick={bozza} data-testid="tab-bozza"><Wand2 className="h-4 w-4 mr-1" />Riparti dalle voci</Button>
          <Button size="sm" onClick={save} disabled={saving || (!dirty && data.salvata)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="tab-salva">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}{dirty ? "Salva modifiche" : "Salvata"}
          </Button>
        </div>
      </div>
      <table className="w-full text-sm min-w-[860px] table-fixed">
        <thead><tr>
          <th className="w-[32%]" />
          {pkgs.map((p) => (
            <th key={p.id} className="px-1 pt-3 pb-2">
              <div className="rounded-t-lg border-t-4 bg-white shadow-sm py-2 text-center" style={{ borderColor: p.color || "#1FAE52" }} data-testid={`tab-pkg-${p.id}`}>
                <div className="font-extrabold" style={{ color: p.color }}>{p.name}</div>
                <div className="text-base font-bold">{fmt(p.price_per_m2)}</div>
                <div className="text-[10px] text-zinc-500 font-normal">al m² · IVA esclusa</div>
              </div>
            </th>
          ))}
        </tr></thead>
        <tbody>{righe.map((r, i) => <Riga key={i} r={r} i={i} n={righe.length} pkgs={pkgs} set={set} move={move} del={del} />)}</tbody>
      </table>
      <div className="flex gap-2 px-5 py-3">
        <Button size="sm" variant="outline" onClick={() => add("voce")} data-testid="tab-aggiungi-voce"><Plus className="h-4 w-4 mr-1" />Aggiungi riga</Button>
        <Button size="sm" variant="outline" onClick={() => add("sezione")} data-testid="tab-aggiungi-sezione"><Heading className="h-4 w-4 mr-1" />Aggiungi titolo sezione</Button>
      </div>
      <div className="px-5 pb-3 text-[11px] text-zinc-500">Nelle caselle: vuota = — non incluso · "sì" = solo ✓ · qualsiasi altro testo (es. "fino a 30 €/m²", "4 porte") = ✓ + testo. I prezzi in alto arrivano sempre dalla Gestione Pacchetti.</div>
      <Promemoria pkgs={pkgs} sugg={data.suggerimenti} />
    </div>
  );
}
